import { FC, useCallback, useState } from 'react';
import { Avatar, List, ListItemAvatar, ListItemButton, ListItemText, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { useHistory } from 'react-router-dom';

import request from '../../api/request';
import HeaderCountButton from './HeaderCountButton';

const USERS_PATH = '/administration/setup/admin/users';

type OnlineUser = { _id: number; firstName: string | null; lastName: string | null; email: string | null };

const displayName = (u: OnlineUser) =>
    [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `#${u._id}`;

/**
 * Who is signed in right now, in the caller's own tenant. Rendered only for `admin.user:view` — the
 * endpoint enforces the same key and the tenant scope.
 */
const OnlineUsersIndicator: FC = () => {
    const history = useHistory();
    const [users, setUsers] = useState<OnlineUser[]>([]);

    // One call serves both the badge and the list: the online set is small, and a separate count
    // endpoint would just be the same query without the names.
    const refresh = useCallback(() => {
        request
            .get('/users-online/list')
            .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
            .catch(() => {});
    }, []);

    return (
        <HeaderCountButton
            title="Online users"
            icon={<PeopleAltOutlinedIcon fontSize="small" />}
            count={users.length}
            color="success"
            refresh={refresh}
            refreshOn={['user:online', 'user:offline']}
            onOpen={refresh}
            footer={[{ label: 'Open Users', onClick: () => history.push(`${USERS_PATH}?online=1`) }]}
        >
            {(close) =>
                users.length ? (
                    <List dense disablePadding>
                        {users.map((u) => (
                            <ListItemButton
                                key={u._id}
                                onClick={() => {
                                    close();
                                    history.push(`${USERS_PATH}?online=1`);
                                }}
                            >
                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                    <Avatar
                                        sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'success.main' }}
                                    >
                                        {displayName(u).charAt(0).toUpperCase()}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={displayName(u)} secondary={u.email} />
                            </ListItemButton>
                        ))}
                    </List>
                ) : (
                    <Typography sx={{ p: 2, color: 'text.secondary', fontSize: '0.85rem' }}>
                        No one is online.
                    </Typography>
                )
            }
        </HeaderCountButton>
    );
};

export default OnlineUsersIndicator;
